import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle } from "@phosphor-icons/react";
import { OrbMark, BrandWordmark } from "../components/Brand";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const PUB = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });

export default function Activate() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [view, setView] = useState(null);
  const [err, setErr] = useState("");
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    PUB.get(`/public/activate/${token}`).then((r) => setView(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Invalid link"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await PUB.post(`/public/activate/${token}`, { password, pin });
      setDone(true);
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Activation failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#07050A] text-[#EDEDF2] flex items-center justify-center px-4" data-testid="activate-page">
      <div className="w-full max-w-md space-y-6">
        <div className="flex justify-center"><BrandWordmark size={32} sub /></div>
        {done ? (
          <div className="glass rounded-2xl p-8 text-center space-y-3" data-testid="activate-done">
            <CheckCircle size={40} className="text-[#A9C97E] mx-auto" />
            <h1 className="font-heading text-xl font-bold">Account activated</h1>
            <p className="text-sm text-muted-foreground">Sign in with your email and new password.</p>
            <Button onClick={() => navigate("/login")} className="bg-primary hover:bg-[#8B5CF6] text-white w-full" data-testid="activate-login">Sign in</Button>
          </div>
        ) : view ? (
          <form onSubmit={submit} className="glass rounded-2xl p-8 space-y-4" data-testid="activate-form">
            <div>
              <h1 className="font-heading text-xl font-bold">Activate {view.venue_name}</h1>
              <p className="text-sm text-muted-foreground mt-1">Welcome, {view.owner_name} — set your password and kiosk PIN for {view.email}.</p>
            </div>
            {err && <p className="text-sm text-[#F87171]" data-testid="activate-error">{err}</p>}
            <div className="space-y-1.5">
              <Label className="text-xs">Password (min 8 characters)</Label>
              <Input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="activate-password" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Kiosk PIN (4 digits — for MISE Shift)</Label>
              <Input required maxLength={4} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} data-testid="activate-pin" />
            </div>
            <Button type="submit" disabled={busy || password.length < 8 || pin.length !== 4} data-testid="activate-submit"
              className="w-full bg-primary hover:bg-[#8B5CF6] text-white h-11">
              {busy ? "Activating…" : "Activate account"}
            </Button>
          </form>
        ) : (
          <p className="text-center text-muted-foreground">{err || "Loading…"}</p>
        )}
      </div>
    </div>
  );
}
